/**
 * Object Utilities
 * Deep operations, cloning, merging, transformations
 * Compatible with Web and React Native
 */
/**
 * Creates a deep clone of an object
 */
export function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    if (obj instanceof Date) {
        return new Date(obj.getTime());
    }
    if (obj instanceof RegExp) {
        return new RegExp(obj.source, obj.flags);
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => deepClone(item));
    }
    if (obj instanceof Map) {
        const clonedMap = new Map();
        obj.forEach((value, key) => {
            clonedMap.set(deepClone(key), deepClone(value));
        });
        return clonedMap;
    }
    if (obj instanceof Set) {
        const clonedSet = new Set();
        obj.forEach((value) => {
            clonedSet.add(deepClone(value));
        });
        return clonedSet;
    }
    const cloned = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            cloned[key] = deepClone(obj[key]);
        }
    }
    return cloned;
}
/**
 * Deeply merges objects
 */
export function deepMerge(target, ...sources) {
    if (!sources.length)
        return target;
    const result = deepClone(target);
    for (const source of sources) {
        if (!source || typeof source !== 'object')
            continue;
        for (const key in source) {
            if (!Object.prototype.hasOwnProperty.call(source, key))
                continue;
            const sourceValue = source[key];
            const targetValue = result[key];
            if (sourceValue &&
                typeof sourceValue === 'object' &&
                !Array.isArray(sourceValue) &&
                targetValue &&
                typeof targetValue === 'object' &&
                !Array.isArray(targetValue)) {
                result[key] = deepMerge(targetValue, sourceValue);
            }
            else {
                result[key] = deepClone(sourceValue);
            }
        }
    }
    return result;
}
/**
 * Checks if an object is empty (no own properties)
 * Note: Use isEmptyArray from array.ts for arrays
 */
export function isEmptyObject(obj) {
    if (!obj)
        return true;
    return Object.keys(obj).length === 0;
}
/**
 * Checks if an object is not empty
 * Note: Use isNotEmptyArray from array.ts for arrays
 */
export function isNotEmptyObject(obj) {
    return !isEmptyObject(obj);
}
/**
 * Removes specified keys from an object
 */
export function omit(obj, keys) {
    const result = { ...obj };
    for (const key of keys) {
        delete result[key];
    }
    return result;
}
/**
 * Picks only specified keys from an object
 */
export function pick(obj, keys) {
    const result = {};
    for (const key of keys) {
        if (key in obj) {
            result[key] = obj[key];
        }
    }
    return result;
}
/**
 * Gets a nested value using a path string
 * @example get({ a: { b: { c: 1 } } }, 'a.b.c') // 1
 * @example get({ a: [{ b: 1 }] }, 'a.0.b') // 1
 */
export function get(obj, path, defaultValue) {
    const keys = path.split('.').flatMap((key) => {
        // Handle array notation: a[0] -> ['a', '0']
        const match = key.match(/^([^[]+)(?:\[(\d+)\])?$/);
        if (match) {
            const result = [match[1]];
            if (match[2] !== undefined) {
                result.push(match[2]);
            }
            return result;
        }
        return [key];
    });
    let current = obj;
    for (const key of keys) {
        if (current === null || current === undefined) {
            return defaultValue;
        }
        current = current[key];
    }
    return (current === undefined ? defaultValue : current);
}
/**
 * Sets a nested value using a path string
 * @example set({}, 'a.b.c', 1) // { a: { b: { c: 1 } } }
 */
export function set(obj, path, value) {
    const result = deepClone(obj);
    const keys = path.split('.');
    let current = result;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current) || typeof current[key] !== 'object') {
            // Check if next key is a number (array index)
            current[key] = /^\d+$/.test(keys[i + 1]) ? [] : {};
        }
        current = current[key];
    }
    current[keys[keys.length - 1]] = value;
    return result;
}
/**
 * Checks if an object has a nested path
 */
export function has(obj, path) {
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
        if (current === null ||
            current === undefined ||
            !Object.prototype.hasOwnProperty.call(current, key)) {
            return false;
        }
        current = current[key];
    }
    return true;
}
/**
 * Removes a nested value using a path string
 */
export function unset(obj, path) {
    const result = deepClone(obj);
    const keys = path.split('.');
    let current = result;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current)) {
            return result;
        }
        current = current[key];
    }
    delete current[keys[keys.length - 1]];
    return result;
}
/**
 * Flattens a nested object into a single-level object with dot notation keys
 * @example flatten({ a: { b: 1 } }) // { 'a.b': 1 }
 */
export function flattenObject(obj, prefix = '', separator = '.') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        const newKey = prefix ? `${prefix}${separator}${key}` : key;
        if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
            Object.assign(result, flattenObject(value, newKey, separator));
        }
        else {
            result[newKey] = value;
        }
    }
    return result;
}
/**
 * Unflattens a dot-notation object into a nested object
 * @example unflatten({ 'a.b': 1 }) // { a: { b: 1 } }
 */
export function unflattenObject(obj, separator = '.') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        const keys = key.split(separator);
        let current = result;
        for (let i = 0; i < keys.length - 1; i++) {
            const k = keys[i];
            if (!(k in current)) {
                current[k] = {};
            }
            current = current[k];
        }
        current[keys[keys.length - 1]] = value;
    }
    return result;
}
/**
 * Maps object values with a function
 */
export function mapValues(obj, fn) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[key] = fn(obj[key], key);
        }
    }
    return result;
}
/**
 * Maps object keys with a function
 */
export function mapKeys(obj, fn) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[fn(key)] = obj[key];
        }
    }
    return result;
}
/**
 * Filters object by keys
 */
export function filterKeys(obj, predicate) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && predicate(key)) {
            result[key] = obj[key];
        }
    }
    return result;
}
/**
 * Filters object by values
 */
export function filterValues(obj, predicate) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && predicate(obj[key], key)) {
            result[key] = obj[key];
        }
    }
    return result;
}
/**
 * Returns entries of an object as [key, value] pairs
 */
export function entries(obj) {
    return Object.entries(obj);
}
/**
 * Creates an object from key-value pairs
 */
export function fromEntries(entries) {
    return Object.fromEntries(entries);
}
/**
 * Returns the keys of an object
 */
export function keys(obj) {
    return Object.keys(obj);
}
/**
 * Returns the values of an object
 */
export function values(obj) {
    return Object.values(obj);
}
/**
 * Inverts an object (keys become values, values become keys)
 */
export function invertObject(obj) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[String(obj[key])] = key;
        }
    }
    return result;
}
/**
 * Compares two objects for deep equality
 */
export function deepEqual(a, b) {
    if (a === b)
        return true;
    if (a === null || b === null)
        return a === b;
    if (typeof a !== 'object' || typeof b !== 'object')
        return false;
    if (Array.isArray(a) !== Array.isArray(b))
        return false;
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length)
            return false;
        return a.every((item, index) => deepEqual(item, b[index]));
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length)
        return false;
    return keysA.every((key) => deepEqual(a[key], b[key]));
}
/**
 * Returns the differences between two objects
 */
export function diff(original, modified) {
    const result = {};
    const allKeys = Array.from(new Set([
        ...Object.keys(original),
        ...Object.keys(modified),
    ]));
    for (const key of allKeys) {
        if (!deepEqual(original[key], modified[key])) {
            result[key] = modified[key];
        }
    }
    return result;
}
/**
 * Removes undefined and null values from an object
 * Note: Use compactArray from array.ts for arrays
 */
export function compactObject(obj) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (value !== undefined && value !== null) {
                result[key] = value;
            }
        }
    }
    return result;
}
/**
 * Removes falsy values from an object
 */
export function compactFalsy(obj) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (value) {
                result[key] = value;
            }
        }
    }
    return result;
}
/**
 * Sets default values for missing properties
 */
export function defaults(obj, defaultValues) {
    const result = { ...defaultValues };
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined) {
            result[key] = obj[key];
        }
    }
    return result;
}
/**
 * Freezes an object deeply (makes it immutable)
 */
export function deepFreeze(obj) {
    Object.freeze(obj);
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) &&
            obj[key] !== null &&
            typeof obj[key] === 'object' &&
            !Object.isFrozen(obj[key])) {
            deepFreeze(obj[key]);
        }
    }
    return obj;
}
/**
 * Renames keys in an object
 */
export function renameKeys(obj, keyMap) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const newKey = keyMap[key] || key;
            result[newKey] = obj[key];
        }
    }
    return result;
}
/**
 * Converts object keys to camelCase
 */
export function camelizeKeys(obj) {
    if (Array.isArray(obj)) {
        return obj.map(camelizeKeys);
    }
    if (obj !== null && typeof obj === 'object') {
        const result = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
                result[camelKey] = camelizeKeys(obj[key]);
            }
        }
        return result;
    }
    return obj;
}
/**
 * Converts a PascalCase key to camelCase.
 * Handles: "Name" → "name", "ID" → "id", "StatusCode" → "statusCode",
 * "PostalAddress01" → "postalAddress01", "HTMLParser" → "htmlParser"
 * Already-camelCase keys pass through unchanged.
 */
function pascalToCamelKey(key) {
    if (!key)
        return key;
    // Count leading uppercase characters
    let upperCount = 0;
    for (let i = 0; i < key.length; i++) {
        if (key[i] >= 'A' && key[i] <= 'Z')
            upperCount++;
        else
            break;
    }
    if (upperCount === 0)
        return key; // already camelCase
    if (upperCount === 1)
        return key[0].toLowerCase() + key.slice(1); // "Name" → "name"
    if (upperCount >= key.length)
        return key.toLowerCase(); // "ID" → "id"
    // "HTMLParser" → "htmlParser" (lowercase all leading uppercase except last before transition)
    return key.slice(0, upperCount - 1).toLowerCase() + key.slice(upperCount - 1);
}
/**
 * Recursively converts all object keys from PascalCase to camelCase.
 * Safe to apply universally — camelCase keys pass through unchanged.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeKeys(obj) {
    if (Array.isArray(obj)) {
        return obj.map(normalizeKeys);
    }
    if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = {};
        for (const key of Object.keys(obj)) {
            result[pascalToCamelKey(key)] = normalizeKeys(obj[key]);
        }
        return result;
    }
    return obj;
}
/**
 * Converts object keys to snake_case
 */
export function snakeizeKeys(obj) {
    if (Array.isArray(obj)) {
        return obj.map(snakeizeKeys);
    }
    if (obj !== null && typeof obj === 'object') {
        const result = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
                result[snakeKey] = snakeizeKeys(obj[key]);
            }
        }
        return result;
    }
    return obj;
}
/**
 * Counts the number of properties in an object
 */
export function size(obj) {
    return Object.keys(obj).length;
}
/**
 * Checks if value is a plain object (not array, not null, not class instance)
 */
export function isPlainObject(value) {
    if (value === null || typeof value !== 'object') {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
//# sourceMappingURL=object.js.map